import {
  isStreamHealthMessage,
  type SessionHealthRecord,
  type StreamHealthEvent,
  type StreamHealthMessage,
} from '@playcastdotio/PeerCore';
import { SessionHealthTimeline } from './SessionHealthTimeline';
import { sinkStreamHealthLine } from './streamHealthSink';

export type StreamHealthManagerOptions = {
  maxEventsPerPeer?: number;
  /** Override the on-disk sink (tests). Defaults to the always-on NDJSON forwarder. */
  sink?: (line: string) => void;
  nowMs?: () => number;
};

/**
 * StreamHealthManager — the host's always-on per-session stream-health aggregator.
 *
 * One place where guest-reported stream-health (`streamHealth` wire messages) and host-side
 * decision events (downgrades, latency-budget breaches, transport lost/resumed) are folded
 * into a single ordered, bounded `SessionHealthRecord` per peer, persisted to disk, and
 * fanned out to subscribers (a HUD/overlay). It holds no thresholds and makes no adaptation
 * decision — it is the observability backbone, not a policy owner.
 */
export class StreamHealthManager {
  private readonly timelines = new Map<string, SessionHealthTimeline>();
  private readonly listeners = new Map<string, Set<(rec: SessionHealthRecord) => void>>();
  private readonly maxEventsPerPeer: number;
  private readonly sink: (line: string) => void;
  private readonly nowMs: () => number;

  constructor(opts: StreamHealthManagerOptions = {}) {
    this.maxEventsPerPeer = opts.maxEventsPerPeer ?? 600;
    this.sink = opts.sink ?? sinkStreamHealthLine;
    this.nowMs = opts.nowMs ?? (() => Date.now());
  }

  /** Ingest a guest streamHealth wire message; malformed messages are dropped by the guard. */
  ingestGuestMessage(peerId: string, msg: StreamHealthMessage): void {
    if (!isStreamHealthMessage(msg)) return;
    const timeline = this.timelineFor(peerId);
    for (const snapshot of msg.snapshots) {
      timeline.applySnapshot(snapshot);
    }
    this.persist(peerId, 'guest-snapshots', { snapshots: msg.snapshots.length });
    this.fanOut(peerId);
  }

  /** Record a host-side event (decision, transport, signaling) for a peer. */
  recordHostEvent(peerId: string, event: StreamHealthEvent): void {
    const timeline = this.timelineFor(peerId);
    timeline.append(event);
    this.persist(peerId, event.kind, { stream: event.stream, atMs: event.atMs });
    this.fanOut(peerId);
  }

  /** The current per-session record for a peer, if any. */
  recordFor(peerId: string): SessionHealthRecord | undefined {
    return this.timelines.get(peerId)?.current();
  }

  /** Subscribe to per-peer record updates (for a HUD/overlay). Returns unsubscribe. */
  subscribe(peerId: string, cb: (rec: SessionHealthRecord) => void): () => void {
    let set = this.listeners.get(peerId);
    if (!set) {
      set = new Set();
      this.listeners.set(peerId, set);
    }
    set.add(cb);
    return () => {
      set.delete(cb);
      if (set.size === 0) this.listeners.delete(peerId);
    };
  }

  clearPeer(peerId: string): void {
    this.timelines.delete(peerId);
    this.listeners.delete(peerId);
  }

  clearAll(): void {
    this.timelines.clear();
    this.listeners.clear();
  }

  private timelineFor(peerId: string): SessionHealthTimeline {
    let timeline = this.timelines.get(peerId);
    if (!timeline) {
      timeline = new SessionHealthTimeline({
        sessionId: peerId,
        peerId,
        startedAtMs: this.nowMs(),
        maxEvents: this.maxEventsPerPeer,
      });
      this.timelines.set(peerId, timeline);
    }
    return timeline;
  }

  private fanOut(peerId: string): void {
    const set = this.listeners.get(peerId);
    if (!set || set.size === 0) return;
    const rec = this.timelines.get(peerId)?.current();
    if (!rec) return;
    for (const cb of set) {
      try {
        cb(rec);
      } catch {
        // A throwing subscriber must not take the aggregator down with it.
      }
    }
  }

  private persist(peerId: string, kind: string, detail: Record<string, number | string | boolean>): void {
    try {
      this.sink(
        JSON.stringify({
          t: new Date(this.nowMs()).toISOString(),
          tag: '[StreamHealth]',
          peerId,
          kind,
          ...detail,
        }),
      );
    } catch {
      // The sink is fire-and-forget; a failed append never surfaces to the caller.
    }
  }
}
